import { useState } from 'react';
import { getInitials, avatarColor, gravatarUrl } from './format-utils';
import { generateAvatarGradient } from '../work/shared/utils';

export interface AvatarProps {
  name?: string;
  email?: string;
  avatar?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  showTooltip?: boolean;
  className?: string;
  /** Use gradient background instead of flat color (default: true when no email) */
  gradient?: boolean;
}

const SIZE_CLASSES: Record<string, string> = {
  xs: 'w-5 h-5 text-[10px]',
  sm: 'w-6 h-6 text-xs',
  md: 'w-8 h-8 text-sm',
  lg: 'w-10 h-10 text-base',
  xl: 'w-12 h-12 text-lg',
};

const SIZE_PX: Record<string, number> = { xs: 20, sm: 24, md: 32, lg: 40, xl: 48 };

export function Avatar({
  name,
  email,
  avatar,
  size = 'md',
  showTooltip = true,
  className = '',
  gradient,
}: AvatarProps) {
  const [imgFailed, setImgFailed] = useState(false);
  const sizeClass = SIZE_CLASSES[size];
  const px = SIZE_PX[size];
  const initials = getInitials(name, email);
  const displayName = name || email || '';
  const useGradient = gradient ?? !email;

  // Direct avatar URL
  if (avatar && !imgFailed) {
    return (
      <div
        className={`${sizeClass} rounded-full overflow-hidden shrink-0 ${className}`}
        title={showTooltip ? displayName : undefined}
      >
        <img
          src={avatar}
          alt={initials}
          onError={() => setImgFailed(true)}
          className="w-full h-full rounded-full object-cover"
        />
      </div>
    );
  }

  // Gravatar (email-based)
  if (email && !imgFailed) {
    return (
      <img
        src={gravatarUrl(email, px * 2)}
        alt={initials}
        onError={() => setImgFailed(true)}
        title={showTooltip ? displayName : undefined}
        className={`${sizeClass} rounded-full object-cover shrink-0 ${className}`}
      />
    );
  }

  // Initials fallback — gradient or flat color
  if (useGradient && displayName) {
    return (
      <div
        className={`${sizeClass} rounded-full flex items-center justify-center font-semibold text-white shadow-sm shrink-0 ${className}`}
        style={{ background: generateAvatarGradient(displayName) }}
        title={showTooltip ? displayName : undefined}
      >
        {initials}
      </div>
    );
  }

  const colorClass = avatarColor(displayName);
  return (
    <div
      className={`${sizeClass} rounded-full flex items-center justify-center font-medium shrink-0 ${colorClass} ${className}`}
      title={showTooltip ? displayName : undefined}
    >
      {initials}
    </div>
  );
}
